// /src/pages/History.js
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import seo from '../seo';

const CHANGELOG = [
  {
    version: '1.1.0',
    date: '2025-01-15',
    items: {
      fr: [
        'Basculer entre mode album et piste avec confirmation et sauvegarde automatique.',
        'Mode avancé/basique pour afficher uniquement les champs essentiels.',
        'Intégration des sources Discogs, MusicBrainz et mode personnalisé.',
        'Éditeur de pochette simplifié avec application globale et confirmations.',
        'Ajout du bouton Boutique, des téléchargements rapides et nombreuses améliorations UI/UX.'
      ],
      en: [
        'Added album vs. track mode with confirmation prompts and automatic saves.',
        'Introduced basic/advanced display to focus on essential fields.',
        'Integrated Discogs, MusicBrainz, and custom metadata sources.',
        'Simplified cover editor with apply-to-all confirmations.',
        'Added Store button, quick download actions, and multiple UI/UX refinements.'
      ],
      es: [
        'Nuevo modo álbum/pista con confirmaciones y guardado automático.',
        'Vista básica/avanzada para mostrar solo los campos esenciales.',
        'Integración de fuentes Discogs, MusicBrainz y modo personalizado.',
        'Editor de carátulas simplificado con aplicación global y confirmaciones.',
        'Botón Tienda, descargas rápidas y varias mejoras de interfaz.'
      ]
    }
  },
  {
    version: '1.0.0',
    date: '2024-12-01',
    items: {
      fr: [
        'Édition complète des tags MP3 (titre, artiste, album, année, piste, genre, paroles, etc.).',
        'Ajout et suppression de pochette avec application possible à tous les fichiers.',
        "Téléchargement par lot avec balises intégrées dans chaque MP3.",
        'Exports disponibles en CSV, HTML et JSON.',
        'Recherche instantanée avec statistiques de collection.',
        'Interface multilingue (FR / EN / ES).',
        'Respect de la confidentialité : traitement 100 % local, aucun upload.',
        'Thèmes clair/sombre responsives avec en-tête et pied de page adaptatifs.'
      ],
      en: [
        'Full MP3 tag editing (title, artist, album, year, track, genre, lyrics, …).',
        'Cover art add/remove with optional apply-to-all.',
        'Batch download with embedded tags in every MP3.',
        'Exports available in CSV, HTML, and JSON.',
        'Search filtering with collection statistics.',
        'Multi-language interface (FR / EN / ES).',
        'Privacy friendly: 100% local processing, no uploads.',
        'Responsive layout with light/dark themes for header and footer.'
      ],
      es: [
        'Edición completa de etiquetas MP3 (título, artista, álbum, año, pista, género, letras, …).',
        'Agregar o quitar carátulas con opción de aplicar a todos los archivos.',
        'Descargas en lote con metadatos incrustados en cada MP3.',
        'Exportaciones disponibles en CSV, HTML y JSON.',
        'Búsqueda con estadísticas de la colección.',
        'Interfaz multilingüe (FR / EN / ES).',
        'Privacidad garantizada: procesamiento 100 % local, sin subidas.',
        'Diseño adaptable con temas claro/oscuro en encabezado y pie.'
      ]
    }
  }
];

export default function History() {
  const { t, language } = useLanguage();
  const { theme } = useTheme();

  const pageTitle = t.historyPage?.title || 'Historique';
  const description =
    t.historyPage?.subtitle || t.footer?.description || 'Notes de versions et changements récents.';
  const url = seo.canonical('/history');

  const card =
    theme === 'dark'
      ? 'bg-gray-800 border border-gray-700 text-gray-100'
      : 'bg-white border border-gray-200 text-gray-900';
  const h2 =
    theme === 'dark' ? 'text-2xl font-semibold text-white' : 'text-2xl font-semibold text-gray-900';
  const muted = theme === 'dark' ? 'text-gray-300' : 'text-gray-600';

  return (
    // fond: gris pâle (light) / sombre (dark)
    <div className={`${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-50'} transition-colors`}>
      <main className="max-w-4xl mx-auto px-4 py-10">
        <Helmet>
          <title>{pageTitle}</title>
          <link rel="canonical" href={url} />
          <meta name="description" content={description} />
          <meta name="robots" content="index,follow" />
          <script type="application/ld+json">
            {JSON.stringify(seo.webPage({ name: pageTitle, url, description }))}
          </script>
        </Helmet>

        <div className={`rounded-xl p-6 ${card}`}>
          <h1 className={theme === 'dark' ? 'text-3xl font-bold text-white' : 'text-3xl font-bold text-gray-900'}>
            {t.historyPage?.title}
          </h1>
          <p className={`${muted} mt-3`}>{t.historyPage?.subtitle}</p>

          <section className={`mt-8 rounded-lg p-5 ${card}`}>
            <h2 className={h2}>{t.historyPage?.sourceReleases}</h2>
            <ul className="mt-4 space-y-4">
              {CHANGELOG.map((entry) => {
                const localizedItems = entry.items[language] || entry.items.en;
                return (
                  <li key={entry.version} className={`rounded-lg p-4 ${card}`}>
                    <div className="flex flex-col gap-2">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="text-lg font-semibold">v{entry.version}</span>
                        <span className={`${muted} text-sm`}>{t.historyPage?.publishedOn} {new Date(entry.date).toLocaleDateString()}</span>
                      </div>
                    <ul className="list-disc list-inside text-sm space-y-1 text-gray-900 dark:text-gray-100">
                      {localizedItems.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                  </div>
                </li>
              );
              })}
            </ul>
          </section>

          <section className={`mt-8 rounded-lg p-5 ${card}`}>
            <h2 className={h2}>Créée pour KracRadio</h2>
            <p className={`${muted} mt-3`}>
              KEMP3 est développée par H1Site.com pour KracRadio, une webradio indépendante de Montréal qui met de l’avant la musique indie et underground via plusieurs canaux.
            </p>
            <div className="flex flex-wrap gap-4 mt-4 text-sm">
              <a
                href="https://kracradio.com"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 dark:text-blue-300 hover:underline"
              >
                KracRadio (FR)
              </a>
              <a
                href="https://kracradio.com"
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 dark:text-blue-300 hover:underline"
              >
                KracRadio (EN)
              </a>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
